<script setup lang="ts">
  import type { IHeaderSection, IHeaderTabs } from '@/components/main-header/types'
  import { useMainHeaderStore } from '@/components/main-header/store'
  import useHeaderDataLayer from '@/composables/data-layers/useHeaderDataLayer'
  import { useSlidingIndicatorBar } from '@/composables/useSlidingIndicatorBar'

  const props = defineProps<{
    headerData: {
      tabs: IHeaderTabs[]
      sections: IHeaderSection[]
    }
  }>()

  const mainHeaderStore = useMainHeaderStore()

  mainHeaderStore.activeSection = props.headerData.sections[0] ?? null

  function toggleActiveSection(section: IHeaderSection) {
    if (mainHeaderStore.activeSection?.label === section?.label) {
      return
    }

    mainHeaderStore.activeSection = section
  }

  const activeSectionIndex = computed(() =>
    props.headerData.sections.findIndex((section) => section.label === mainHeaderStore.activeSection?.label)
  )

  const activeIndicatorIndex = computed(() => (activeSectionIndex.value >= 0 ? activeSectionIndex.value : null))

  const { containerRef, itemRefs, indicatorBar, handleIndicatorEnter, handleIndicatorLeave } =
    useSlidingIndicatorBar(activeIndicatorIndex)

  const { pushHeaderTabClickDataLayer, getHeaderTabEventCategory } = useHeaderDataLayer()

  function onButtonClick(item: IHeaderSection) {
    const eventCategoryValue = item.defaultFullSlug?.split('/').pop() ?? ''

    pushHeaderTabClickDataLayer(getHeaderTabEventCategory(eventCategoryValue))
    toggleActiveSection(item)
  }
</script>

<template>
  <nav
    ref="containerRef"
    data-test="sections"
    class="relative flex h-full justify-center gap-x-8 px-24 md:flex"
    @mouseleave="handleIndicatorLeave"
  >
    <button
      v-for="(item, index) in headerData.sections"
      :key="item.label"
      ref="itemRefs"
      class="relative inline-flex items-center py-3"
      :class="{ 'font-medium': item.label === mainHeaderStore.activeSection?.label }"
      :aria-expanded="item.label === mainHeaderStore.activeSection?.label"
      @mouseenter="handleIndicatorEnter(index)"
      @focus="handleIndicatorEnter(index)"
      @blur="handleIndicatorLeave"
      @click="onButtonClick(item)"
    >
      {{ item.label }}
    </button>

    <span
      v-show="indicatorBar.visible && indicatorBar.width > 0"
      aria-hidden="true"
      class="pointer-events-none absolute bottom-0 left-0 h-1 rounded-t bg-bt-green transition-all duration-300 ease-out will-change-transform"
      :style="{ transform: `translateX(${indicatorBar.left}px)`, width: `${indicatorBar.width}px` }"
    />
  </nav>
</template>
